/**
 * Money handling for the web client.
 *
 * Every amount in this application is an integer count of minor units — pesewas for GHS. No
 * money value is ever held in a floating-point cedi figure, parsed with parseFloat, or scaled by
 * multiplying a decimal: `0.1 + 0.2` losing a pesewa in a teller drawer is a reconciliation
 * incident, not a rounding curiosity. The API transports minor units as integers too (the
 * backend's `Money.ToMinorUnits()`), so a value crosses the wire, the parser, and the formatter
 * without ever changing representation.
 *
 * GHS is the only transacting currency today. The currency code travels alongside the amount
 * anyway, because that pairing is what makes adding a second currency a configuration change.
 */

/** ISO 4217 code of the only currency that transacts today. */
export const BASE_CURRENCY = 'GHS'

/** Minor units per major unit for GHS. */
const MINOR_UNITS_PER_MAJOR = 100

const MINOR_UNIT_DIGITS = 2

/**
 * Fixed locale rather than the browser's. A teller reading a figure over the phone to head
 * office and the printed receipt beside them must group and punctuate identically, on every
 * machine, regardless of the operating system's regional settings.
 */
const MONEY_LOCALE = 'en-GH'

const CURRENCY_SYMBOLS: Record<string, string> = {
  GHS: 'GH₵',
  USD: 'US$',
  EUR: '€',
  GBP: '£',
}

const groupingFormatter = new Intl.NumberFormat(MONEY_LOCALE, {
  useGrouping: true,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/** An integer count of minor units. Negative values are permitted; fractions are not. */
export type Pesewas = number

export type MoneyParseFailure = 'empty' | 'malformed' | 'too_many_decimals' | 'out_of_range'

export type MoneyParseResult =
  | { ok: true; value: Pesewas }
  | { ok: false; reason: MoneyParseFailure }

/** Longest whole-cedi run we accept, which keeps `whole * 100` inside the safe integer range. */
const MAX_WHOLE_DIGITS = 13

/**
 * Characters a money field may contain while the user is still typing. Used to reject keystrokes
 * rather than to validate a finished value — a lone `-` or a trailing `.` is legal mid-entry.
 */
const PARTIAL_INPUT = /^-?[\d,\s]*(\.\d{0,2})?$/

const COMPLETE_INPUT = /^(-)?(\d*)(?:\.(\d{0,2}))?$/

export const isPartialMoneyInput = (raw: string) => PARTIAL_INPUT.test(raw)

/**
 * Parses typed text into pesewas using integer arithmetic only. The whole and fractional parts
 * are read as separate integers and combined, so no decimal string is ever handed to a
 * floating-point parse.
 */
export function parseMoneyInput(raw: string): MoneyParseResult {
  const stripped = raw.replace(/[\s,]/g, '').replace(/^GH₵/i, '')

  if (stripped === '' || stripped === '-') {
    return { ok: false, reason: 'empty' }
  }

  if (/\.\d{3,}$/.test(stripped)) {
    return { ok: false, reason: 'too_many_decimals' }
  }

  const match = COMPLETE_INPUT.exec(stripped)
  if (!match) {
    return { ok: false, reason: 'malformed' }
  }

  const [, sign, whole = '', fraction = ''] = match
  if (whole === '' && fraction === '') {
    return { ok: false, reason: 'empty' }
  }

  if (whole.length > MAX_WHOLE_DIGITS) {
    return { ok: false, reason: 'out_of_range' }
  }

  const majorUnits = whole === '' ? 0 : Number(whole)
  const minorUnits = fraction === '' ? 0 : Number(fraction.padEnd(MINOR_UNIT_DIGITS, '0'))
  const magnitude = majorUnits * MINOR_UNITS_PER_MAJOR + minorUnits

  if (!Number.isSafeInteger(magnitude)) {
    return { ok: false, reason: 'out_of_range' }
  }

  return { ok: true, value: sign === '-' ? -magnitude : magnitude }
}

export interface FormatMoneyOptions {
  currency?: string
  /** Omit the currency symbol — for edit fields and columns that carry the unit in the header. */
  symbol?: boolean
  /** Accounting convention shows a negative as (1,234.56); statements and ledgers use it. */
  negativeStyle?: 'minus' | 'parentheses'
  /** Force a leading + on positive amounts, for movement columns. */
  signed?: boolean
}

/**
 * Renders pesewas as `GH₵ 1,234.56`. The split into whole and fractional parts is exact: the
 * remainder is taken first so the division that follows is over an exact multiple of 100.
 */
export function formatMoney(value: Pesewas, options: FormatMoneyOptions = {}): string {
  const { currency = BASE_CURRENCY, symbol = true, negativeStyle = 'minus', signed = false } = options

  const isNegative = value < 0
  const magnitude = Math.abs(Math.trunc(value))
  const fraction = magnitude % MINOR_UNITS_PER_MAJOR
  const majorUnits = (magnitude - fraction) / MINOR_UNITS_PER_MAJOR

  const digits = `${groupingFormatter.format(majorUnits)}.${String(fraction).padStart(MINOR_UNIT_DIGITS, '0')}`
  const body = symbol ? `${CURRENCY_SYMBOLS[currency] ?? currency} ${digits}` : digits

  if (isNegative) {
    return negativeStyle === 'parentheses' ? `(${body})` : `-${body}`
  }

  return signed && value > 0 ? `+${body}` : body
}

/** Digits only, for the value shown inside an input while it is not focused. */
export const formatMoneyForInput = (value: Pesewas) => formatMoney(value, { symbol: false })

export const currencySymbol = (currency: string = BASE_CURRENCY) =>
  CURRENCY_SYMBOLS[currency] ?? currency

/**
 * Splits an amount into parts that sum back to it exactly, with the rounding remainder on the
 * final part. This mirrors `Money.AllocateEvenly` on the server so a schedule previewed in the
 * browser matches the one the ledger generates and the one printed into the paper passbook.
 */
export function allocateEvenly(total: Pesewas, parts: number): Pesewas[] {
  if (!Number.isInteger(parts) || parts <= 0) {
    throw new RangeError('An amount must be allocated into at least one part.')
  }

  const each = Math.trunc(total / parts)
  const allocation = new Array<number>(parts).fill(each)
  allocation[parts - 1] = total - each * (parts - 1)
  return allocation
}
